import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { Body as BodyBase } from 'js/design_system/Body';
import AvatarBase, { Name as NameBase } from './shared/Avatar';
import { CoauthorSuggestion, CoauthorTag } from 'js/social/shared/types';
import { Button as ButtonBase } from 'js/social/shared/design_system/Button';
import CloseIcon from 'js/design_system/icons/close.svg';
import CheckIcon from 'js/design_system/icons/check.svg';
import { SnackbarContext } from 'js/social/contexts/snackbar';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 16px;
`;

const UserSection = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: 8px;
`;

const Coauthorships = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;
`;

const Coauthorship = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const Avatar = styled(AvatarBase)`
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const University = styled(BodyBase)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  color: $ds-black;
  font-size: 12px;
  line-height: 18px;
`;

const Name = styled(NameBase)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 500;
  color: $ds-black;
`;

const Title = styled(BodyBase)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled(BodyBase)`
  color: $ds-gray-dark;
`;

const Button = styled(ButtonBase)`
  flex-grow: 1;
`;

const MAX_TAGS = 3;

interface Props {
  coauthorSuggestion: CoauthorSuggestion;
  submitCoauthorConfirmation: (
    accepted: boolean,
    tag: CoauthorTag,
  ) => Promise<any>;
  removeCoauthorTag: (removedTagIdx: number) => void;
}

const CoauthorConfirmationCard: React.FC<Props> = ({
  coauthorSuggestion,
  submitCoauthorConfirmation,
  removeCoauthorTag,
}) => {
  const { showSnackbar } = useContext(SnackbarContext);

  const onClick = (accepted: boolean, tagData: CoauthorTag, tagIdx: number) => {
    submitCoauthorConfirmation(accepted, tagData)
      .then(() => {
        if (accepted) {
          showSnackbar(`Confirmed ${tagData.name} as a co-author`, 'success');
        }
        removeCoauthorTag(tagIdx);
      })
      .catch(() => {
        showSnackbar('There was an error with your request', 'failure');
      });
  };

  return (
    <Container>
      <UserSection>
        <Avatar
          fullName={coauthorSuggestion.name as string}
          photoUrl={coauthorSuggestion.profile_picture_url}
          profileUrl={coauthorSuggestion.profile_url}
        />
        <UserInfo>
          <Name href={coauthorSuggestion.profile_url}>
            {coauthorSuggestion.name}
          </Name>
          {coauthorSuggestion.affiliated_university && (
            <University>{coauthorSuggestion.affiliated_university}</University>
          )}
        </UserInfo>
      </UserSection>
      <div>
        <Body size="xs">Co-author for:</Body>
        <Coauthorships>
          {coauthorSuggestion.tags.slice(0, MAX_TAGS).map((tag, idx) => (
            <Coauthorship key={idx}>
              <Title>
                <Body size="xs" bold>
                  {tag.title}
                </Body>
              </Title>
              <ButtonContainer>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<CheckIcon size={20} />}
                  onClick={() => onClick(true, tag, idx)}
                >
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="inverse"
                  icon={<CloseIcon size={20} />}
                  onClick={() => onClick(false, tag, idx)}
                >
                  Dismiss
                </Button>
              </ButtonContainer>
            </Coauthorship>
          ))}
        </Coauthorships>
      </div>
    </Container>
  );
};

export default CoauthorConfirmationCard;
